import React, { useState, useEffect, useRef, useMemo } from 'react'
import {
  Text,
  SelectField,
  ListingSearchField,
  TransactionSearchField,
  InputField,
} from '@adc/ui-morpheus'
import classnames from 'classnames/bind'
import logger from '@adc/logger'
import type { Maybe, Listing, AuctionTransaction } from '@adc/sdk/types'
import { fetchUsersByEmail } from '../../providers/user'
import ListingLinks from '../listingLinks'
import {
  formatContext,
  extractListingIdFromUrn,
  extractTransactionIdFromUrn,
  detectSourceTypeFromUrn,
  extractIdFromUrn,
  properCase,
} from './utils'
import { useListingData } from './useListingData'
import { useTransactionData } from './useTransactionData'
import styles from './styles.scss'

export type ContextSourceType = 'listing' | 'transaction' | string

export interface ContextOutput {
  sources: string[]
  sourceType: ContextSourceType
  // TBD: may want to pull run as out - not sure if it makes sense here
  run_as?: string
  /** Display name of the run_as user (e.g. "John Smith") */
  run_as_display_name?: string
  /** Display title derived from the context source (e.g. first line of listing address) */
  contextTitle?: string
}

export interface ContextSelectorProps {
  onContextChange: (context: ContextOutput | null) => void
  initialContext?: ContextOutput | null
  placeholder?: string
  elmId?: string
  viewMode?: boolean
  enableRunAs?: boolean
  hideLabel?: boolean
  /** Hide the listing context fields (source type + search) */
  hideContext?: boolean
  /** Hide the run-as user section */
  hideRunAs?: boolean
  /** Supported context resource types from workflow metadata */
  contextResourcesSupported?: string[]
}

const cx = classnames.bind(styles)

const UNAUTHENTICATED_USER_VALUE = '__unauthenticated__'

const ContextSelector: React.FC<ContextSelectorProps> = ({
  onContextChange,
  initialContext = null,
  placeholder = 'Search by Listing Id (in quotes) or by address',
  elmId = 'context_selector',
  viewMode = false,
  enableRunAs = false,
  hideLabel = false,
  hideContext = false,
  hideRunAs = false,
  contextResourcesSupported = ['listing'],
}) => {
  const [searchValue, setSearchValue] = useState<string>('')
  const [selectedListing, setSelectedListing] =
    useState<Maybe<Partial<Listing>>>(null)
  const [selectedTransaction, setSelectedTransaction] =
    useState<Maybe<Partial<AuctionTransaction>>>(null)
  const [rawId, setRawId] = useState<string>('')
  const [selectedResourceType, setSelectedResourceType] =
    useState<ContextSourceType>(contextResourcesSupported[0] || 'listing')
  const [selectedUserEmail, setSelectedUserEmail] = useState<string>('')
  const [userOptions, setUserOptions] = useState<
    Array<{ value: string; text: string }>
  >([])
  const [runAsMode, setRunAsMode] = useState<
    'authenticated' | 'unauthenticated'
  >('authenticated')

  // Derive dropdown options from contextResourcesSupported
  const resourceTypeOptions = useMemo(
    () =>
      contextResourcesSupported.map(type => ({
        text: properCase(type),
        value: type,
      })),
    [contextResourcesSupported]
  )

  const showResourceTypeDropdown = contextResourcesSupported.length > 1

  // Fetch full listing data using custom hook
  const { listing: fetchedListing } = useListingData(initialContext)

  // Fetch full transaction data using custom hook
  const { transaction: fetchedTransaction, isLoading: isLoadingTransaction } =
    useTransactionData(initialContext)

  // Track whether this component emitted the context (to avoid re-initializing from our own output)
  const selfEmittedRef = useRef(false)

  // Initialize from initialContext prop (for view mode) - runs only when initialContext changes
  useEffect(() => {
    // Skip if this context came from our own emit
    if (selfEmittedRef.current) {
      selfEmittedRef.current = false
      return
    }

    if (!initialContext) {
      return
    }

    // Handle run_as if present (before sources check so it works even without listing context)
    if (initialContext.run_as) {
      if (initialContext.run_as.startsWith('adc:anonymous_session:')) {
        setRunAsMode('unauthenticated')
        setSelectedUserEmail(UNAUTHENTICATED_USER_VALUE)
      } else {
        setRunAsMode('authenticated')
        const email = initialContext.run_as.replace('adc:email:', '')
        setSelectedUserEmail(email)
        setUserOptions(prev =>
          prev.some(o => o.value === email)
            ? prev
            : [...prev, { value: email, text: email }]
        )
      }
    }

    if (!initialContext.sources || initialContext.sources.length === 0) {
      return
    }

    const sourceUrn = initialContext.sources[0]
    const detectedType = detectSourceTypeFromUrn(sourceUrn)
    setSelectedResourceType(detectedType)

    if (detectedType === 'listing') {
      const listingId = extractListingIdFromUrn(sourceUrn)
      if (listingId) {
        const currentListingId = selectedListing?.listing_id
        const hasFetchedData = fetchedListing?.listing_id === listingId

        if (currentListingId !== listingId && !hasFetchedData) {
          setSelectedListing({ listing_id: listingId })
        }
        setSearchValue('')
      }
    } else if (detectedType === 'transaction') {
      const txId = extractTransactionIdFromUrn(sourceUrn)
      if (txId) {
        setSelectedTransaction(prev => {
          // Avoid loop: don't overwrite if already set to same URN
          if (prev?.transaction_urn === sourceUrn) return prev
          return { transaction_urn: sourceUrn } as Partial<AuctionTransaction>
        })
        setSearchValue('')
      }
    } else {
      // Generic type — extract raw ID
      const id = extractIdFromUrn(sourceUrn)
      if (id) {
        setRawId(id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialContext])

  // Update to full listing data when it becomes available
  useEffect(() => {
    if (fetchedListing) {
      setSelectedListing(fetchedListing)
      setSearchValue('')
    }
  }, [fetchedListing])

  // Update to full transaction data when it becomes available
  useEffect(() => {
    if (fetchedTransaction) {
      setSelectedTransaction(fetchedTransaction)
      setSearchValue('')
    }
  }, [fetchedTransaction])

  // Track whether initialContext has been processed to avoid emitting null on first render
  const hasInitializedRef = useRef(!initialContext)

  // Emit context changes to parent
  useEffect(() => {
    // Skip the first emit if we have an initialContext that hasn't been processed yet
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true
      return
    }
    const context = formatContext(
      selectedListing,
      selectedResourceType,
      selectedUserEmail,
      runAsMode,
      selectedTransaction,
      rawId
    )
    selfEmittedRef.current = true
    onContextChange(context)
  }, [
    selectedListing,
    selectedTransaction,
    rawId,
    selectedResourceType,
    selectedUserEmail,
    runAsMode,
    onContextChange,
  ])

  const handleListingChange = (listing: Maybe<Partial<Listing>>) => {
    setSelectedListing(listing)
    if (!listing) {
      setSearchValue('')
    }
  }

  const handleTransactionChange = (
    transaction: Maybe<Partial<AuctionTransaction>>
  ) => {
    setSelectedTransaction(prev => {
      if (!transaction) return null
      // Always accept if new object has listing data that prev lacks
      if (
        prev?.transaction_urn === transaction?.transaction_urn &&
        (prev?.listing || !transaction?.listing)
      ) {
        return prev
      }
      return transaction
    })
    if (!transaction) {
      setSearchValue('')
    }
  }

  const handleResourceTypeChange = (_e: unknown, data: { value: string }) => {
    setSelectedResourceType(data.value)
    // Reset all selections when switching type
    setSelectedListing(null)
    setSelectedTransaction(null)
    setRawId('')
    setSearchValue('')
  }

  // Determine if we have a selected resource (for hiding the dropdown after selection)
  const hasSelection =
    (selectedResourceType === 'listing' && !!selectedListing) ||
    (selectedResourceType === 'transaction' && !!selectedTransaction) ||
    (selectedResourceType !== 'listing' &&
      selectedResourceType !== 'transaction' &&
      !!rawId)

  // Hide entire component when in view mode with no selection and context is expected
  if (viewMode && !hasSelection && !hideContext) {
    return null
  }

  return (
    <div className={cx('context-selector', { disabled: viewMode })}>
      {!hideLabel && !hideContext && <Text weight='heavy'>Context</Text>}

      {/* Resource type selector — show dropdown for 2+, label for 1 */}
      {!hideContext && !searchValue && !hasSelection && (
        <div className={cx('context-selector-row')}>
          <div className={cx('context-selector-field-group')}>
            {showResourceTypeDropdown ? (
              <>
                <Text
                  size='tiny'
                  color='gray'
                  className={cx('context-selector-label')}
                >
                  Select type
                </Text>
                <SelectField
                  hideWrapper
                  value={selectedResourceType}
                  options={resourceTypeOptions}
                  onChange={handleResourceTypeChange}
                  compact={false}
                  elmId={`${elmId}_source_type`}
                />
              </>
            ) : (
              <Text
                size='tiny'
                color='gray'
                className={cx('context-selector-label')}
              >
                Specify {properCase(selectedResourceType)}
              </Text>
            )}
          </div>
        </div>
      )}

      {/* Search field — varies by resource type */}
      {!hideContext && selectedResourceType === 'listing' && (
        <div className={cx('context-selector-row')}>
          <div className={cx('context-selector-search-wrapper')}>
            <ListingSearchField
              label=''
              value={searchValue}
              onChange={(value: string) => setSearchValue(value)}
              onChangeCurrentListing={handleListingChange}
              currentListing={selectedListing}
              placeholder={placeholder}
              elmId={`${elmId}_listing_search`}
              viewMode={viewMode}
              compact={false}
            />
          </div>
        </div>
      )}

      {!hideContext &&
        selectedResourceType === 'transaction' &&
        !isLoadingTransaction && (
          <div className={cx('context-selector-row')}>
            <div className={cx('context-selector-search-wrapper')}>
              <TransactionSearchField
                label=''
                value={searchValue}
                onChange={(value: string) => setSearchValue(value)}
                onChangeCurrentTransaction={handleTransactionChange}
                currentTransaction={selectedTransaction}
                placeholder='Search by Transaction ID or address'
                elmId={`${elmId}_transaction_search`}
                viewMode={viewMode}
                compact={false}
              />
            </div>
          </div>
        )}

      {!hideContext &&
        selectedResourceType !== 'listing' &&
        selectedResourceType !== 'transaction' && (
          <div className={cx('context-selector-row')}>
            <div className={cx('context-selector-search-wrapper')}>
              <InputField
                label=''
                value={rawId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setRawId(e.target.value)
                }
                placeholder='Specify ID'
                elmId={`${elmId}_raw_id_input`}
                viewMode={viewMode}
              />
            </div>
          </div>
        )}

      {!hideContext && selectedListing && selectedListing.listing_id && (
        <div
          className={cx('context-selector-row', 'listing-links-enabled')}
          style={{ gap: '4px', fontSize: '14px' }}
        >
          (
          <ListingLinks
            listing={selectedListing as Required<Partial<Listing>>}
            elmIdPrefix={`${elmId}_listing`}
            style={{ fontSize: '14px' }}
          />
          )
        </div>
      )}

      {/* User selection - only show if enableRunAs is true and not hidden */}
      {enableRunAs && !hideRunAs && (!viewMode || selectedUserEmail) && (
        <div className={cx('context-selector-user-section')}>
          <Text weight='heavy' className={cx('context-selector-title')}>
            User (to run as)
          </Text>

          <div className={cx('context-selector-row')}>
            <div className={cx('context-selector-search-wrapper')}>
              <SelectField
                compact
                hideWrapper
                value={selectedUserEmail}
                onChange={(_e: unknown, data: { value: string }) => {
                  if (data.value === UNAUTHENTICATED_USER_VALUE) {
                    setRunAsMode('unauthenticated')
                    setSelectedUserEmail(UNAUTHENTICATED_USER_VALUE)
                  } else {
                    setRunAsMode('authenticated')
                    setSelectedUserEmail(data.value)
                  }
                }}
                onSearchChange={async (
                  _e: unknown,
                  data: { searchQuery?: string }
                ) => {
                  const searchTerm = data?.searchQuery || ''
                  if (searchTerm.length < 2) {
                    setUserOptions([])
                    return
                  }
                  try {
                    const users = await fetchUsersByEmail(searchTerm, 10)
                    const options = (
                      users as Array<{ user_id: string; email: string }>
                    ).map((user: { user_id: string; email: string }) => ({
                      value: user.email,
                      text: user.email,
                    }))
                    setUserOptions(options)
                  } catch (error) {
                    logger.error('Failed to fetch users:', error)
                    setUserOptions([])
                  }
                }}
                options={[
                  {
                    value: UNAUTHENTICATED_USER_VALUE,
                    text: 'Unauthenticated User',
                  },
                  ...userOptions,
                ]}
                placeholder='Search for user to run as'
                elmId={`${elmId}_user_search`}
                search
                selection
                clearable
                disabled={viewMode}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ContextSelector
